import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Package, Layers, Wrench } from "lucide-react";
import { toast } from "sonner";
import { CustomConfigurationBuilder } from "./configurator";
import { buildConfigComponents } from "./configurator/buildConfigComponents";
import type { ConfigComponent, Configuration } from "./configurator/types";
import type { 
  ModelComponentWithDefaults, 
  ComponentTypeMaterialWithName, 
  AttributeDefWithAttribute,
  CoordinatesValue 
} from "@/types/supabase-helpers";

// Re-export types for backwards compatibility
export type { ConfigComponent, Configuration };

interface OrderConfiguratorTabProps {
  orderId?: string;
  configuration: Configuration | null;
  onConfigurationChange: (config: Configuration | null) => void;
  selectedCustomerId?: string;
}

export function OrderConfiguratorTab({
  orderId,
  configuration,
  onConfigurationChange,
  selectedCustomerId,
}: OrderConfiguratorTabProps) {
  const [selectedModelId, setSelectedModelId] = useState<string | null>(
    configuration?.productModelId || null
  );
  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(new Set());
  const [isLoadedConfig, setIsLoadedConfig] = useState(false);
  const initializedModelRef = useRef<string | null>(null);

  // Sync selectedModelId when configuration is loaded from outside
  // HUOM: Käytetään koko configuration-objektia dependencyna
  // jotta sync ajetaan aina kun useLoadOrder lataa uuden konfiguraation
  useEffect(() => {
    if (configuration?.productModelId) {
      setSelectedModelId(configuration.productModelId);
      setExpandedComponents(new Set(configuration.components.map((c) => c.id)));
      setIsLoadedConfig(true);
      initializedModelRef.current = configuration.productModelId;
    } else if (!configuration && selectedModelId) {
      setSelectedModelId(null);
      setIsLoadedConfig(false);
      initializedModelRef.current = null;
    }
  }, [configuration]);

  // Fetch product models with is_custom_template field
  const { data: productModels } = useQuery({
    queryKey: ["product-models-active"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_models")
        .select("id, name, description, is_custom_template")
        .is("deleted_at", null)
        .eq("is_active", true)
        .order("position");
      if (error) throw error;
      return data;
    },
  });

  // Haetaan valitun asiakkaan tiedot (optimoitu: vain valittu asiakas)
  const { data: selectedCustomer } = useQuery({
    queryKey: ["customer-simple", selectedCustomerId],
    queryFn: async () => {
      if (!selectedCustomerId) return null;
      const { data } = await supabase
        .from("customers")
        .select("id, first_name, last_name, company_name")
        .eq("id", selectedCustomerId)
        .maybeSingle();
      return data;
    },
    enabled: !!selectedCustomerId,
  });

  const customerLastName = selectedCustomer?.last_name || 
    selectedCustomer?.company_name?.split(' ')[0] || undefined;

  // Detect if current model is a custom template
  const isCustomTemplate = useMemo(() => {
    if (!selectedModelId || !productModels) return false;
    const model = productModels.find(m => m.id === selectedModelId);
    return model?.is_custom_template === true;
  }, [selectedModelId, productModels]);

  // Fetch model components with their component types, allowed materials, and attributes
  const { data: modelData } = useQuery({
    queryKey: ["model-components-full", selectedModelId],
    queryFn: async () => {
      if (!selectedModelId) return null;

      // Get model components
      const { data: components, error: componentsError } = await supabase
        .from("model_components")
        .select(`
          id,
          component_type_id,
          min_quantity,
          max_quantity,
          default_material_id,
          default_width,
          default_height,
          default_thickness,
          position,
          label,
          component_types (
            id,
            name,
            label,
            dimension_mode
          )
        `)
        .eq("product_model_id", selectedModelId)
        .order("position");

      if (componentsError) throw componentsError;

      // Get all component type IDs
      const componentTypeIds = components?.map((c) => c.component_type_id) || [];

      // Fetch allowed materials for each component type
      const { data: materialsData } = await supabase
        .from("component_type_materials")
        .select(`
          component_type_id,
          material_id,
          is_default,
          materials (id, name)
        `)
        .in("component_type_id", componentTypeIds)
        .order("position");

      // Fetch attribute definitions for each component type
      const { data: attributeDefsData } = await supabase
        .from("component_attribute_defs")
        .select(`
          id,
          component_type_id,
          attribute_id,
          is_required,
          default_value,
          position,
          attributes (id, name, label, attribute_type, options, is_required)
        `)
        .in("component_type_id", componentTypeIds)
        .order("position");

      return {
        components,
        materials: materialsData || [],
        attributeDefs: attributeDefsData || [],
      };
    },
    enabled: !!selectedModelId,
  });

  // Initialize configuration when model is selected (not when loading existing)
  useEffect(() => {
    if (!selectedModelId || !modelData || isLoadedConfig) {
      // Reset isLoadedConfig after it has been processed
      if (isLoadedConfig && selectedModelId) {
        setIsLoadedConfig(false);
      }
      return;
    }

    const productModel = productModels?.find((m) => m.id === selectedModelId);
    if (!productModel) return;

    // Guard - Estä turha reinitilisointi jos sama malli on jo initialisoitu
    if (initializedModelRef.current === selectedModelId) {
      return;
    }

    // Build config components
    const configComponents = buildConfigComponents(
      modelData.components as ModelComponentWithDefaults[],
      modelData.materials as ComponentTypeMaterialWithName[],
      modelData.attributeDefs as AttributeDefWithAttribute[]
    );

    // Expand all components by default
    setExpandedComponents(new Set(configComponents.map((c) => c.id)));

    onConfigurationChange({
      productModelId: selectedModelId,
      productModelName: productModel.name,
      customName: configuration?.customName || "",
      components: configComponents,
    });

    // Merkitse malli initialisoiduksi
    initializedModelRef.current = selectedModelId;
  }, [selectedModelId, modelData, productModels, isLoadedConfig]);

  const handleModelSelect = (modelId: string) => {
    const wasCustomTemplate = isCustomTemplate;
    const willBeCustomTemplate = productModels?.find(m => m.id === modelId)?.is_custom_template;
    
    if (modelId === "none") {
      setSelectedModelId(null);
      onConfigurationChange(null);
      initializedModelRef.current = null;
    } else {
      setSelectedModelId(modelId);
      
      // Nollaa customName jos vaihtuu räätälöidystä normaaliin tai päinvastoin
      if (wasCustomTemplate !== willBeCustomTemplate && configuration) {
        onConfigurationChange({
          ...configuration,
          customName: undefined
        });
      }
    }
  };

  const updateComponentMaterial = (componentId: string, materialId: string) => {
    if (!configuration) return;

    const updatedComponents = configuration.components.map((c) =>
      c.id === componentId ? { ...c, materialId } : c
    );

    onConfigurationChange({
      ...configuration,
      components: updatedComponents,
    });
  };

  const updateComponentQuantity = (componentId: string, quantity: number) => {
    if (!configuration) return;

    const component = configuration.components.find((c) => c.id === componentId);
    if (!component) return;

    const clampedQuantity = Math.max(
      component.minQuantity,
      Math.min(component.maxQuantity, quantity)
    );

    const updatedComponents = configuration.components.map((c) =>
      c.id === componentId ? { ...c, quantity: clampedQuantity } : c
    );

    onConfigurationChange({
      ...configuration,
      components: updatedComponents,
    });
  };

  const updateComponentDimension = (
    componentId: string,
    dimension: "width" | "height" | "thickness",
    value: number | null
  ) => {
    if (!configuration) return;

    const updatedComponents = configuration.components.map((c) =>
      c.id === componentId ? { ...c, [dimension]: value } : c
    );

    onConfigurationChange({
      ...configuration,
      components: updatedComponents,
    });
  };

  const updateAttributeValue = (
    componentId: string,
    attributeId: string,
    value: string | number | CoordinatesValue | null
  ) => {
    if (!configuration) return;

    const updatedComponents = configuration.components.map((c) => {
      if (c.id === componentId) {
        const updatedAttributes = c.attributes.map((attr) =>
          attr.id === attributeId ? { ...attr, value } : attr
        );
        return { ...c, attributes: updatedAttributes };
      }
      return c;
    });

    onConfigurationChange({
      ...configuration,
      components: updatedComponents,
    });
  };

  const toggleComponentExpanded = (componentId: string) => {
    setExpandedComponents((prev) => {
      const next = new Set(prev);
      if (next.has(componentId)) {
        next.delete(componentId);
      } else {
        next.add(componentId);
      }
      return next;
    });
  };

  const renderAttributeInput = (
    component: ConfigComponent,
    attr: ConfigComponent["attributes"][0]
  ) => {
    switch (attr.type) {
      case "text":
        return (
          <Input
            value={(attr.value as string) || ""}
            onChange={(e) => updateAttributeValue(component.id, attr.id, e.target.value)}
            placeholder={attr.label}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            value={(attr.value as number) || ""}
            onChange={(e) =>
              updateAttributeValue(
                component.id,
                attr.id,
                e.target.value ? parseFloat(e.target.value) : null
              )
            }
            placeholder={attr.label}
          />
        );
      case "date":
        return (
          <Input
            type="date"
            value={(attr.value as string) || ""}
            onChange={(e) => updateAttributeValue(component.id, attr.id, e.target.value)}
          />
        );
      case "selection":
        return (
          <Select
            value={(attr.value as string) || ""}
            onValueChange={(value) => updateAttributeValue(component.id, attr.id, value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Valitse ${attr.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {attr.options?.map((option) => {
                const optLabel = typeof option === 'string' ? option : (option?.label || String(option));
                const optValue = typeof option === 'string' ? option : (option?.value || String(option));
                return (
                  <SelectItem key={optValue} value={optValue}>
                    {optLabel}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        );
      case "coordinates":
        const coordValue = attr.value as CoordinatesValue | null;
        return (
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="number"
              placeholder="X"
              value={coordValue?.x ?? ""}
              onChange={(e) => {
                const currentVal: CoordinatesValue = coordValue || { x: null, y: null };
                const newValue: CoordinatesValue = {
                  ...currentVal,
                  x: e.target.value ? parseFloat(e.target.value) : null,
                };
                updateAttributeValue(component.id, attr.id, newValue);
              }}
            />
            <Input
              type="number"
              placeholder="Y"
              value={coordValue?.y ?? ""}
              onChange={(e) => {
                const currentVal: CoordinatesValue = coordValue || { x: null, y: null };
                const newValue: CoordinatesValue = {
                  ...currentVal,
                  y: e.target.value ? parseFloat(e.target.value) : null,
                };
                updateAttributeValue(component.id, attr.id, newValue);
              }}
            />
          </div>
        );
      default:
        return (
          <Input
            value={(attr.value as string) || ""}
            onChange={(e) => updateAttributeValue(component.id, attr.id, e.target.value)}
          />
        );
    }
  };

  // Render model selector (shared between custom and normal mode)
  const renderModelSelector = () => (
    <div className="space-y-2">
      <Label className="text-base font-semibold">Tuotemalli</Label>
      <Select
        value={selectedModelId || "none"}
        onValueChange={handleModelSelect}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Valitse tuotemalli" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Ei tuotemallia</SelectItem>
          {/* Räätälöidyt mallit ensin */}
          {productModels?.filter(m => m.is_custom_template).map((model) => (
            <SelectItem key={model.id} value={model.id}>
              <div className="flex items-center gap-2">
                <Wrench className="h-4 w-4 text-warning" />
                <span>{model.name}</span>
                <Badge variant="outline" className="ml-2 text-xs">
                  Vapaa
                </Badge>
              </div>
            </SelectItem>
          ))}
          {/* Erottaja jos molempia on */}
          {productModels?.some(m => m.is_custom_template) && productModels?.some(m => !m.is_custom_template) && (
            <SelectSeparator />
          )}
          {/* Normaalit mallit */}
          {productModels?.filter(m => !m.is_custom_template).map((model) => (
            <SelectItem key={model.id} value={model.id}>
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <span>{model.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {productModels?.find((m) => m.id === selectedModelId)?.description && (
        <p className="text-sm text-muted-foreground">
          {productModels.find((m) => m.id === selectedModelId)?.description}
        </p>
      )}
    </div>
  );

  // Custom template mode: use CustomConfigurationBuilder
  if (isCustomTemplate && configuration) {
    return (
      <div className="space-y-6">
        {renderModelSelector()}
        <CustomConfigurationBuilder
          configuration={configuration}
          onConfigurationChange={onConfigurationChange}
          customerLastName={customerLastName}
        />
      </div>
    );
  }

  // Normal mode
  return (
    <div className="space-y-6">
      {/* Product Model Selection */}
      {renderModelSelector()}

      {/* Configuration Components */}
      {configuration && configuration.components.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-muted-foreground" />
            <Label className="text-base font-semibold">Komponentit</Label>
            <Badge variant="secondary">{configuration.components.length} kpl</Badge>
          </div>

          <div className="space-y-3">
            {configuration.components.map((component) => (
              <Card key={component.id} className="overflow-hidden">
                <Collapsible
                  open={expandedComponents.has(component.id)}
                  onOpenChange={() => toggleComponentExpanded(component.id)}
                >
                  <CollapsibleTrigger asChild>
                    <CardHeader className={`cursor-pointer hover:bg-muted/50 py-3 px-4 ${component.quantity === 0 ? 'opacity-50' : ''}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {expandedComponents.has(component.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <CardTitle className={`text-sm font-medium ${component.quantity === 0 ? 'line-through text-muted-foreground' : ''}`}>
                            {component.componentTypeLabel}
                          </CardTitle>
                        </div>
                        <div className="flex items-center gap-2">
                          {component.quantity > 0 && component.materialId && (
                            <Badge variant="outline" className="text-xs">
                              {component.allowedMaterials.find(
                                (m) => m.id === component.materialId
                              )?.name || "Materiaali"}
                            </Badge>
                          )}
                          {component.quantity === 0 ? (
                            <Badge variant="destructive" className="text-xs">
                              Ei valittu
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              {component.quantity} kpl
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0 pb-4 px-4 space-y-4">
                      {/* Quantity - Show for optional components (min=0) or when max > 1 */}
                      {(component.maxQuantity > 1 || component.minQuantity === 0) && (
                        <div className="space-y-2">
                          <Label>
                            Määrä ({component.minQuantity} - {component.maxQuantity})
                          </Label>
                          <Input
                            type="number"
                            min={component.minQuantity}
                            max={component.maxQuantity}
                            value={component.quantity}
                            onChange={(e) =>
                              updateComponentQuantity(
                                component.id,
                                parseInt(e.target.value) || component.minQuantity
                              )
                            }
                          />
                          {component.minQuantity === 0 && (
                            <p className="text-xs text-muted-foreground">
                              Aseta 0, jos tätä komponenttia ei tarvita
                            </p>
                          )}
                        </div>
                      )}

                      {/* Material Selection - Only show when quantity > 0 */}
                      {component.quantity > 0 && component.allowedMaterials.length > 0 && (
                        <div className="space-y-2">
                          <Label>Materiaali</Label>
                          <Select
                            value={component.materialId || ""}
                            onValueChange={(value) =>
                              updateComponentMaterial(component.id, value)
                            }
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Valitse materiaali" />
                            </SelectTrigger>
                            <SelectContent>
                              {component.allowedMaterials.map((material) => (
                                <SelectItem key={material.id} value={material.id}>
                                  {material.name}
                                  {material.isDefault && (
                                    <span className="text-xs text-muted-foreground ml-2">
                                      (oletus)
                                    </span>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Dimensions - Only show when quantity > 0 and dimensionMode is 'free' */}
                      {component.quantity > 0 && component.dimensionMode === 'free' && (
                        <div className="space-y-2">
                          <Label>Mitat (mm)</Label>
                          <div className="grid grid-cols-3 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Leveys</Label>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                placeholder="-"
                                value={component.width != null ? Math.round(component.width * 1000) : ""}
                                onChange={(e) =>
                                  updateComponentDimension(
                                    component.id,
                                    "width",
                                    e.target.value ? parseFloat(e.target.value) / 1000 : null
                                  )
                                }
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Korkeus</Label>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                placeholder="-"
                                value={component.height != null ? Math.round(component.height * 1000) : ""}
                                onChange={(e) =>
                                  updateComponentDimension(
                                    component.id,
                                    "height",
                                    e.target.value ? parseFloat(e.target.value) / 1000 : null
                                  )
                                }
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">Paksuus</Label>
                              <Input
                                type="number"
                                min={0}
                                step={1}
                                placeholder="-"
                                value={component.thickness != null ? Math.round(component.thickness * 1000) : ""}
                                onChange={(e) =>
                                  updateComponentDimension(
                                    component.id,
                                    "thickness",
                                    e.target.value ? parseFloat(e.target.value) / 1000 : null
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Catalog mode notice */}
                      {component.quantity > 0 && component.dimensionMode === 'catalog' && (
                        <p className="text-xs text-muted-foreground italic">
                          Koko määräytyy attribuuteista
                        </p>
                      )}

                      {/* Attributes - Only show when quantity > 0 */}
                      {component.quantity > 0 && component.attributes.length > 0 && (
                        <div className="space-y-3 pt-2 border-t">
                          <Label className="text-sm font-medium text-muted-foreground">
                            Attribuutit
                          </Label>
                          {component.attributes.map((attr) => (
                            <div key={attr.id} className="space-y-1">
                              <Label className="text-sm">
                                {attr.label}
                                {attr.isRequired && (
                                  <span className="text-destructive ml-1">*</span>
                                )}
                              </Label>
                              {renderAttributeInput(component, attr)}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!selectedModelId && (
        <div className="text-center py-8 text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Valitse tuotemalli luodaksesi konfiguraation</p>
        </div>
      )}

      {selectedModelId && configuration && configuration.components.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Tuotemallilla ei ole määriteltyjä komponentteja</p>
        </div>
      )}
    </div>
  );
}
